/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Checkbox,
  FormControlLabel,
  Box,
  Divider,
} from '@mui/material';
import React, {useState, useEffect} from 'react';
import type {AgentAbility, AgentAction} from '../../../types/agent';
import type {AgentAbilityType} from '../hooks/useGetAgentAbilityTypes';
import type {AgentActionType} from '../hooks/useGetAgentActionTypes';

type Props = {
  open: boolean;
  onClose: () => void;
  ability: AgentAbility;
  onSave: (ability: AgentAbility) => void;
  abilityTypes: AgentAbilityType[];
  actionTypes: AgentActionType[];
};

export const AgentAbilityModal: React.FC<Props> = ({open, onClose, ability, onSave, abilityTypes, actionTypes}) => {
  const [abilityData, setAbilityData] = useState<Omit<AgentAbility, 'inputs' | 'postActions'>>({
    name: ability.name,
    instructions: ability.instructions,
    functionType: ability.functionType || abilityTypes[0]?.name || '',
    userVariables: ability.userVariables || {},
    abilityId: ability.abilityId,
  });
  const [inputs, setInputs] = useState(
    ability.inputs.length > 0 ? ability.inputs : [{name: '', instructions: '', required: false}],
  );
  const [postActions, setPostActions] = useState<AgentAction[]>(ability.postActions || []);

  const alphanumericRegex = /^[a-zA-Z0-9]+$/;

  const handleAbilityChange = <T extends keyof typeof abilityData>(field: T, value: (typeof abilityData)[T]) => {
    setAbilityData({...abilityData, [field]: value});
  };

  const handleUserVariableChange = (variableName: string, value: string) => {
    setAbilityData({
      ...abilityData,
      userVariables: {...abilityData.userVariables, [variableName]: value},
    });
  };

  const handleInputChange = (index: number, field: keyof AgentAbility['inputs'][0], value: string | boolean) => {
    const updatedInputs = inputs.map((input, i: number) => (i === index ? {...input, [field]: value} : input));
    setInputs(updatedInputs);
  };

  const handleToggleAction = (actionName: string) => {
    const actionExists = postActions.some((action) => action.name === actionName);
    if (actionExists) {
      setPostActions(postActions.filter((action) => action.name !== actionName));
    } else {
      setPostActions([...postActions, {name: actionName}]);
    }
  };

  const addInput = () => {
    setInputs([...inputs, {name: '', instructions: '', required: false}]);
  };

  const deleteInput = (index: number) => {
    setInputs(inputs.filter((_, i: number) => i !== index));
  };

  const handleSave = () => {
    onSave({...abilityData, inputs, postActions});
  };

  const selectedType = abilityTypes.find((type) => type.name === abilityData.functionType);

  // Pre-populate user variables with default values when creating a new ability
  useEffect(() => {
    if (!ability.abilityId && selectedType) {
      const defaultUserVars = selectedType.userVariables.reduce(
        (acc, variable) => {
          acc[variable.name] = variable.default || '';
          return acc;
        },
        {} as Record<string, string>,
      );
      setAbilityData((prev) => ({
        ...prev,
        userVariables: defaultUserVars,
      }));
    }
  }, [selectedType, ability.abilityId]);

  const isAbilityValid = abilityData.name.trim() !== '' && abilityData.instructions.trim() !== '';
  const areInputsValid = inputs.every((input) => input.name.trim() !== '' && input.instructions.trim() !== '');
  const areVariablesValid =
    selectedType?.userVariables.every((variable) => (abilityData.userVariables[variable.name] || '').trim() !== '') ??
    true;

  const isSaveDisabled = !isAbilityValid || !areInputsValid || !areVariablesValid;

  if (abilityTypes.length === 0) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth={'md'}>
        <DialogTitle>{ability.abilityId ? 'Edit Ability' : 'New Ability'}</DialogTitle>
        <DialogContent>
          <Alert severity={'error'}>No ability types available</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant={'contained'} color={'primary'} disabled>
            {ability.abilityId ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={'md'}>
      <DialogTitle>{ability.abilityId ? 'Edit Ability' : 'New Ability'}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{mt: 1}}>
          <FormControl fullWidth>
            <InputLabel id={'ability-type-label'}>Ability Type</InputLabel>
            <Select
              labelId={'ability-type-label'}
              value={abilityData.functionType}
              label={'Ability Type'}
              onChange={(e) => handleAbilityChange('functionType', e.target.value)}
            >
              {abilityTypes.map((type, index: number) => (
                <MenuItem key={index} value={type.name}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={'Name'}
            fullWidth
            required
            error={abilityData.name !== '' && !alphanumericRegex.test(abilityData.name)}
            helperText={
              abilityData.name !== '' && !alphanumericRegex.test(abilityData.name) ? 'Name must be alphanumeric.' : ''
            }
            value={abilityData.name}
            onChange={(e) => handleAbilityChange('name', e.target.value)}
          />
          <TextField
            label={'Instructions'}
            fullWidth
            required
            multiline
            minRows={1}
            maxRows={6}
            value={abilityData.instructions}
            onChange={(e) => handleAbilityChange('instructions', e.target.value)}
          />
          {selectedType && selectedType.userVariables.length > 0 && (
            <Stack spacing={2}>
              <Typography variant={'h6'}>Settings</Typography>
              {selectedType.userVariables.map((variable, index: number) => (
                <TextField
                  key={index}
                  label={variable.label}
                  fullWidth
                  required
                  value={abilityData.userVariables[variable.name] || ''}
                  onChange={(e) => handleUserVariableChange(variable.name, e.target.value)}
                />
              ))}
            </Stack>
          )}
          <Stack spacing={3}>
            <Typography variant={'h6'}>Inputs</Typography>
            {inputs.map((input, index: number) => (
              <Grid container spacing={2} alignItems={'flex-start'} key={index}>
                <Grid item xs={3}>
                  <TextField
                    label={'Name'}
                    fullWidth
                    required
                    error={input.name !== '' && !alphanumericRegex.test(input.name)}
                    helperText={
                      input.name !== '' && !alphanumericRegex.test(input.name) ? 'Name must be alphanumeric.' : ''
                    }
                    value={input.name}
                    onChange={(e) => handleInputChange(index, 'name', e.target.value)}
                    InputProps={{style: {alignItems: 'flex-start'}}}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={'Instructions'}
                    fullWidth
                    required
                    multiline
                    minRows={1}
                    maxRows={6}
                    value={input.instructions}
                    onChange={(e) => handleInputChange(index, 'instructions', e.target.value)}
                    InputProps={{style: {alignItems: 'flex-start'}}}
                  />
                </Grid>
                <Grid item xs={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={input.required || false}
                        onChange={(e) => handleInputChange(index, 'required', e.target.checked)}
                      />
                    }
                    label={'Required'}
                    sx={{mt: 2}}
                  />
                </Grid>
                <Grid item xs={1}>
                  <IconButton onClick={() => deleteInput(index)} color={'error'} sx={{mt: 1}}>
                    <CloseIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Box>
              <Button onClick={addInput}>Add Input</Button>
            </Box>
          </Stack>

          {actionTypes && actionTypes.length > 0 && (
            <Stack spacing={2}>
              <Divider sx={{my: 2}} />
              <Typography variant={'h6'}>Post-Execution Actions</Typography>
              <Typography variant={'body2'} color={'text.secondary'}>
                Select actions to execute after this ability completes.
              </Typography>
              <FormControl component={'fieldset'} variant={'standard'}>
                <Stack>
                  {actionTypes.map((actionType) => (
                    <FormControlLabel
                      key={actionType.name}
                      control={
                        <Checkbox
                          checked={postActions.some((action) => action.name === actionType.name)}
                          onChange={() => handleToggleAction(actionType.name)}
                          name={actionType.name}
                        />
                      }
                      label={actionType.label}
                    />
                  ))}
                </Stack>
              </FormControl>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant={'contained'} color={'primary'} onClick={handleSave} disabled={isSaveDisabled}>
          {ability.abilityId ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
